import type { ReactNode } from "react";
import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

// Distinctive "answer block" wrapper — short, self-contained, quotable text
// placed as the FIRST paragraph under each H2 so AI Overviews can lift it.
const AnswerBlock: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div className="not-prose mb-5 rounded-xl border border-accent/30 bg-accent/5 p-4 sm:p-5">
    <p className="text-[1.0625rem] leading-relaxed text-foreground">
      {children}
    </p>
  </div>
);

/* ── The 9 quotable answer blocks (kept word-for-word in the FAQ schema) ── */

const a1 =
  "SAINT\u00ae (Stanford Accelerated Intelligent Neuromodulation Therapy) is an FDA-cleared treatment for adults with major depressive disorder who have not improved on antidepressants. It uses an fMRI brain scan to find a personal treatment target, then delivers 50 short sessions of magnetic stimulation over five days, instead of the six to nine weeks of standard TMS.";

const a2 =
  "SAINT stands for Stanford Accelerated Intelligent Neuromodulation Therapy. Researchers at Stanford University developed it, and their 2022 trial called it Stanford Neuromodulation Therapy (SNT); it is the same treatment. Magnus Medical makes the FDA-cleared SAINT\u00ae system, and only clinics on Magnus Medical's provider locator deliver it.";

const a3 =
  "SAINT\u00ae starts with an fMRI scan of about 45 minutes that maps how areas of your brain communicate. Software picks the spot in the prefrontal cortex to target. Treatment is then ten 10-minute sessions a day, separated by 50-minute breaks, for five days in a row, which is 50 sessions in total.";

const a4 =
  "Regular TMS aims at a standard location on the scalp and usually takes one session a day, five days a week, for six to nine weeks. SAINT\u00ae aims at a target chosen from each person's own fMRI brain scan and finishes in five days. SAINT\u00ae is the only FDA-cleared TMS that personalizes the target this way.";

const a5 =
  "Yes. SAINT\u00ae is FDA-cleared for adults with major depressive disorder who did not improve enough on prior antidepressant medication in their current episode, which is how treatment-resistant depression is usually described. A clinician confirms candidacy, including safety checks such as metal implants and seizure history.";

const a6 =
  "In a 2022 double-blind randomized trial, 78.6% of people who received SAINT\u00ae reached remission within about four weeks after treatment (Cole et al., American Journal of Psychiatry, 2022). An earlier open-label study reported about 90% remission in 21 participants (Cole et al., 2020). Results vary by person.";

const a7 =
  "The most common side effects in the trials were headache, fatigue and scalp or treatment-site discomfort, usually mild and short-lived. No anesthesia or sedation is needed. Like all TMS, there is a rare risk of seizure. People with non-removable metal in or near the head are not candidates.";

const a8 =
  "Only clinics listed on Magnus Medical's official provider locator deliver SAINT\u00ae. Some clinics sell lower-cost \u201cfMRI-guided accelerated TMS\u201d programs that copy the 5-day schedule but use their own, non-cleared targeting off-label, so they can't claim SAINT's trial results or FDA clearance. Neuro Wellness is the verified SAINT\u00ae provider in Florida.";

const a9 =
  "SAINT\u00ae at Neuro Wellness starts at $25,000 for the full five-day course, including the fMRI scan and all 50 sessions. Medicare has a payment rate for SAINT\u00ae. Commercial insurance coverage is limited and decided case by case through prior authorization. We check your benefits for free.";

const saintFaqs: LearnFAQ[] = [
  { question: "What is SAINT\u00ae TMS?", answer: a1 },
  {
    question: "What does SAINT stand for, and is it the same as SNT?",
    answer: a2,
  },
  { question: "How does the SAINT\u00ae protocol work?", answer: a3 },
  { question: "How is SAINT\u00ae different from regular TMS?", answer: a4 },
  {
    question: "Is SAINT\u00ae for treatment-resistant depression?",
    answer: a5,
  },
  {
    question: "Does SAINT\u00ae work, and what did the trials show?",
    answer: a6,
  },
  { question: "What are the side effects of SAINT\u00ae?", answer: a7 },
  {
    question: "How do I know a clinic offers real SAINT\u00ae?",
    answer: a8,
  },
  {
    question: "How much does SAINT\u00ae cost, and does insurance cover it?",
    answer: a9,
  },
];

/* ── Page 1: the ONE "What is SAINT?" guide ── */

export const page1: LearnPageData = {
  slug: "what-is-saint-tms",
  title: "What Is SAINT\u00ae TMS? The 5-Day Stanford-Developed Treatment",
  description:
    "SAINT\u00ae (Stanford Accelerated Intelligent Neuromodulation Therapy, also called SNT) explained: how the fMRI-guided 5-day TMS works, results, safety, cost.",
  canonical: "/learn/what-is-saint-tms",
  h1: "What Is SAINT\u00ae TMS? The 5-Day Stanford-Developed Treatment",
  breadcrumbLabel: "What Is SAINT\u00ae TMS?",
  crumbs: baseCrumbs("What Is SAINT\u00ae TMS?"),
  shortAnswer:
    "SAINT\u00ae (Stanford Accelerated Intelligent Neuromodulation Therapy) is an FDA-cleared treatment for adults with major depression who haven't improved on antidepressants \u2014 it uses an fMRI brain scan to find a personal target, then delivers 50 short sessions over five days instead of six to nine weeks of standard TMS. Neuro Wellness TMS Centers of America in Coral Springs is the verified SAINT\u00ae provider in Florida.",
  keyTakeaways: [
    "SAINT\u00ae is FDA-cleared (510(k) K220177, September 2022) for adults with major depressive disorder who haven't improved enough on antidepressants.",
    "It delivers 10 sessions per day for 5 consecutive days (50 total), versus roughly 30\u201336 sessions over 4\u20136 weeks for standard TMS.",
    "The stimulation target is chosen from your own functional MRI \u2014 SAINT\u00ae is the only FDA-cleared TMS that personalizes the target this way.",
    "In a 2022 sham-controlled trial, 78.6% of the active group reached remission at 4 weeks versus 13.3% of sham. Individual results vary.",
    "Neuro Wellness in Coral Springs is the verified SAINT\u00ae provider in Florida. A free consultation confirms whether it's right for you.",
  ],
  sections: [
    {
      heading: "What is SAINT\u00ae TMS?",
      body: (
        <>
          <AnswerBlock>{a1}</AnswerBlock>
          {P(
            "SAINT\u00ae \u2014 Stanford Accelerated Intelligent Neuromodulation Therapy \u2014 is an accelerated form of transcranial magnetic stimulation (TMS) for major depressive disorder. It was developed at the Stanford Brain Stimulation Lab and is delivered commercially on the Magnus Neuromodulation System with SAINT Technology, made by Magnus Medical, Inc.",
          )}
          {P(
            "What sets SAINT\u00ae apart is how compressed it is. Standard TMS takes one session a day for four to six weeks. SAINT\u00ae delivers ten short sessions a day for five consecutive days \u2014 50 sessions in one week \u2014 each using an intermittent theta burst stimulation (iTBS) pulse pattern, with roughly 50-minute breaks between sessions.",
          )}
        </>
      ),
    },
    {
      heading: "What does SAINT stand for, and is it the same as SNT?",
      body: (
        <>
          <AnswerBlock>{a2}</AnswerBlock>
          {P(
            "The 2022 randomized trial published in the American Journal of Psychiatry referred to the treatment as Stanford Neuromodulation Therapy (SNT). SAINT\u00ae and SNT describe the same protocol. The FDA-cleared commercial system is the Magnus Neuromodulation System with SAINT Technology (510(k) K220177), and it is available only through clinics credentialed by Magnus Medical.",
          )}
        </>
      ),
    },
    {
      heading: "How does the SAINT\u00ae protocol work?",
      body: (
        <>
          <AnswerBlock>{a3}</AnswerBlock>
          {P(
            "Before your treatment week, you have a psychiatric evaluation and a resting-state functional MRI. The scan maps how your prefrontal cortex communicates with deeper mood circuits, and proprietary software calculates your individual stimulation target in the left dorsolateral prefrontal cortex. [[Does SAINT\u00ae require an MRI?|/learn/does-saint-tms-require-an-mri]]",
          )}
          {P(
            "During the week, neuronavigation holds the coil on that exact target for every session. Each session lasts about ten minutes; with the breaks, you spend most of each of the five days at the clinic. No anesthesia or sedation is used, and you are awake throughout.",
          )}
        </>
      ),
    },
    {
      heading: "How is SAINT\u00ae different from regular TMS?",
      body: (
        <>
          <AnswerBlock>{a4}</AnswerBlock>
          <div className="my-5 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="px-3 py-2 text-left font-semibold text-primary"></th>
                  <th className="px-3 py-2 text-left font-semibold text-primary">
                    SAINT\u00ae
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-primary">
                    Standard TMS
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border/50">
                  <td className="px-3 py-2 font-medium">Schedule</td>
                  <td className="px-3 py-2">5 days</td>
                  <td className="px-3 py-2">6\u20139 weeks</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="px-3 py-2 font-medium">
                    Number of clinic visits
                  </td>
                  <td className="px-3 py-2">5</td>
                  <td className="px-3 py-2">30\u201336</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="px-3 py-2 font-medium">
                    How the target is chosen
                  </td>
                  <td className="px-3 py-2">Personal fMRI-guided target</td>
                  <td className="px-3 py-2">Standard scalp measurement</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="px-3 py-2 font-medium">FDA clearance</td>
                  <td className="px-3 py-2">Cleared 2022 (K220177)</td>
                  <td className="px-3 py-2">Cleared since 2008</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="px-3 py-2 font-medium">
                    Published remission rate
                  </td>
                  <td className="px-3 py-2">78.6% in 2022 trial</td>
                  <td className="px-3 py-2">Varies by study</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="px-3 py-2 font-medium">
                    Typical self-pay cost
                  </td>
                  <td className="px-3 py-2">From $25,000</td>
                  <td className="px-3 py-2">Ask your provider</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3">
            {" "}
            Full comparison:{" "}
            <a
              href="/learn/saint-vs-traditional-tms"
              className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
            >
              SAINT\u00ae vs TMS side by side
            </a>
            .
          </p>
        </>
      ),
    },
    {
      heading: "Is SAINT\u00ae for treatment-resistant depression?",
      body: (
        <>
          <AnswerBlock>{a5}</AnswerBlock>
          {P(
            "Treatment-resistant depression is typically defined as major depressive disorder that has not responded adequately to at least two antidepressant trials of adequate dose and duration. SAINT\u00ae's FDA-cleared indication covers adults in this situation, though the final determination of eligibility is made by a psychiatrist after a clinical evaluation.",
          )}
          {P(
            "Learn more: who [[qualifies for SAINT\u00ae|/learn/is-saint-for-treatment-resistant-depression]].",
          )}
        </>
      ),
    },
    {
      heading: "Does SAINT\u00ae work? What the trials showed",
      body: (
        <>
          <AnswerBlock>{a6}</AnswerBlock>
          {P(
            "In the 2020 open-label study, 19 of 21 completers (90.5%) met remission criteria after five days, but there was no sham comparison. In the 2022 double-blind, sham-controlled randomized trial, 78.6% of the active group (11 of 14) reached remission at four weeks compared with 13.3% (2 of 15) of the sham group.",
          )}
          {P(
            "Both studies were small and conducted at a single site, and follow-up was short (four weeks in the randomized trial). The results should be read as promising early evidence rather than definitive. No trial has compared SAINT\u00ae head-to-head with a standard six-week TMS course. Individual results vary.",
          )}
        </>
      ),
    },
    {
      heading: "What are the side effects of SAINT\u00ae?",
      body: (
        <>
          <AnswerBlock>{a7}</AnswerBlock>
          {P(
            "The most common side effects are headache, scalp discomfort, and facial muscle twitching, which usually lessen over the first day or two. The most serious risk is seizure, which is rare. Over-the-counter pain relief is often enough for headaches; tell your treatment team about any discomfort so coil position or intensity can be adjusted.",
          )}
          {P(
            "People with ferromagnetic metal in or near the head (such as cochlear implants or aneurysm clips), implanted electronic stimulators, or who cannot have an MRI are not candidates. A history of seizures requires careful evaluation. SAINT\u00ae is not FDA-cleared for bipolar depression, PTSD, anxiety disorders alone, OCD, or anyone under 18. See [[is SAINT\u00ae safe?|/learn/is-saint-tms-safe]].",
          )}
        </>
      ),
    },
    {
      heading: "How do I know a clinic offers real SAINT\u00ae?",
      body: (
        <>
          <AnswerBlock>{a8}</AnswerBlock>
          {P(
            "Some clinics market a lower-cost \u201cfMRI-guided accelerated TMS\u201d program that copies the five-day schedule but uses their own, non-cleared targeting off-label. Because those programs were not part of the SAINT clinical trials and do not use the Magnus Medical system, they cannot claim SAINT\u00ae's trial results or FDA clearance.",
          )}
          {P(
            "Neuro Wellness TMS Centers of America is listed on Magnus Medical's official provider locator. Learn how to [[verify a SAINT\u00ae provider|/learn/what-is-a-verified-saint-provider]] and read our [[SAINT\u00ae disclaimer|/learn/saint-disclaimer]].",
          )}
        </>
      ),
    },
    {
      heading: "How much does SAINT\u00ae cost, and does insurance cover it?",
      body: (
        <>
          <AnswerBlock>{a9}</AnswerBlock>
          {P(
            "The $25,000 starting price covers the fMRI scan and all 50 sessions for the full five-day course. Medicare has a payment rate for SAINT\u00ae. Commercial coverage is limited and decided case by case through prior authorization, which is why we verify your benefits for free before you commit. See [[SAINT\u00ae cost & insurance|/learn/saint-tms-cost]].",
          )}
        </>
      ),
    },
  ],
  faqs: saintFaqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): a double-blind randomized controlled trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://doi.org/10.1176/appi.ajp.2019.19070720",
    },
    {
      citation:
        "FDA 510(k) Premarket Notification K220177 \u2014 Magnus Neuromodulation System with SAINT Technology.",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm?ID=K220177",
    },
  ],
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/what-is-saint-tms#webpage`,
        name: "What Is SAINT\u00ae TMS? The 5-Day Stanford-Developed Treatment",
        url: `${SITE_URL}/learn/what-is-saint-tms`,
        description:
          "SAINT\u00ae (Stanford Accelerated Intelligent Neuromodulation Therapy, also called SNT) explained: how the fMRI-guided 5-day TMS works, results, safety, cost.",
        about: [
          { "@id": `${SITE_URL}/learn/what-is-saint-tms#therapy` },
          { "@id": `${SITE_URL}/#condition-mdd` },
          { "@id": `${SITE_URL}/#condition-trd` },
        ],
        reviewedBy: {
          name: "Daniel Pietras, MD",
          url: `${SITE_URL}/dr-daniel-pietras-md`,
        },
        lastReviewed: "2026-11-03",
      }),
      orgNode,
      {
        "@type": "MedicalTherapy",
        "@id": `${SITE_URL}/learn/what-is-saint-tms#therapy`,
        name: "SAINT\u00ae (Stanford Accelerated Intelligent Neuromodulation Therapy)",
        alternateName: [
          "SAINT TMS",
          "Stanford Neuromodulation Therapy (SNT)",
          "Magnus Neuromodulation System with SAINT Technology",
        ],
        description:
          "An accelerated, fMRI-targeted intermittent theta burst transcranial magnetic stimulation protocol delivering 10 sessions per day for 5 consecutive days, FDA-cleared (510(k) K220177) for major depressive disorder in adults who have failed to achieve satisfactory improvement from prior antidepressant medication in the current episode.",
        relevantSpecialty: "Psychiatry",
        indication: {
          "@type": "MedicalIndication",
          name: "Major depressive disorder in adult patients who have failed to achieve satisfactory improvement from prior antidepressant medication in the current episode",
        },
        contraindication: [
          {
            "@type": "MedicalContraindication",
            name: "Ferromagnetic metal in or near the head (e.g., cochlear implants, aneurysm clips)",
          },
          {
            "@type": "MedicalContraindication",
            name: "Implanted electronic stimulators",
          },
          {
            "@type": "MedicalContraindication",
            name: "Inability to undergo MRI",
          },
        ],
        seriousAdverseOutcome: {
          "@type": "MedicalEntity",
          name: "Seizure (rare)",
        },
        adverseOutcome: [
          { "@type": "MedicalEntity", name: "Headache" },
          { "@type": "MedicalEntity", name: "Scalp discomfort" },
          { "@type": "MedicalEntity", name: "Facial muscle twitching" },
        ],
        study: [
          {
            "@type": "MedicalStudy",
            name: "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(8):716-726.",
            url: "https://doi.org/10.1176/appi.ajp.2019.19070720",
          },
          {
            "@type": "MedicalStudy",
            name: "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
            url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
          },
        ],
      },
      {
        "@type": "MedicalCondition",
        "@id": `${SITE_URL}/#condition-mdd`,
        name: "Major depressive disorder",
        code: { "@type": "MedicalCode", code: "D003865", codingSystem: "MeSH" },
      },
      {
        "@type": "MedicalCondition",
        "@id": `${SITE_URL}/#condition-trd`,
        name: "Treatment-resistant depression",
      },
      {
        "@type": "VideoObject",
        name: "SAINT\u00ae TMS Explained",
        description:
          "What SAINT\u00ae is, how it differs from standard TMS, and what research shows. SAINT\u00ae is an FDA-cleared, fMRI-guided form of TMS completed in five days for adults with major depressive disorder who have not improved on antidepressants.",
        thumbnailUrl:
          "https://img.youtube.com/vi/j7uXq9Cj2sw/maxresdefault.jpg",
        uploadDate: "2024-01-01",
        embedUrl: "https://www.youtube.com/embed/j7uXq9Cj2sw",
        contentUrl: `${SITE_URL}/videos/saint-tms-explained`,
      },
      faqJsonld(saintFaqs, `${SITE_URL}/learn/what-is-saint-tms#faq`),
      breadcrumbJsonld(baseCrumbs("What Is SAINT\u00ae TMS?")),
    ],
  },
  relatedLinks: sharedRelated,
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
};
